/**
 * AR portfolio-loss and write-off analysis (ALC-4547). The approved-analysis PAYLOAD only: actor,
 * decision, reason, evidence and version-bound invalidation stay on the existing financial-control
 * review (`FinancialControlReview`, recorded through `POST …/financial-controls/AR08/reviews`).
 *
 * Interface note: docs/features/continuous-close/06-evaluation-seam.md ("Reviews"), AR08 v4.
 */
// `ArPortfolioLossAnalysisLineKind` itself comes from the generated Prisma enum (already
// re-exported by `index.ts`'s `export * from './generated/enums.js'`) — imported here, not
// redeclared, so the two star-exports never collide (TS2308).
import type { ArPortfolioLossAnalysisLineKind } from './generated/enums.js';
import { ArPortfolioLossAnalysisLineKind as ArPortfolioLossAnalysisLineKindValues } from './generated/enums.js';

export const AR_PORTFOLIO_LOSS_ANALYSIS_LINE_KINDS = Object.values(
  ArPortfolioLossAnalysisLineKindValues,
) as readonly ArPortfolioLossAnalysisLineKind[];

export interface CreateArPortfolioLossAnalysisLineInput {
  kind: ArPortfolioLossAnalysisLineKind;
  invoiceId: string;
  scheduleId?: string | null;
  /** Non-negative decimal string, in the book's functional currency. */
  amount: string;
  /** `JournalHeader.journalId` the accountant says carries this effect; omit if not yet posted. */
  journalEntryId?: string | null;
}

export interface CreateArPortfolioLossAnalysisInput {
  entityId: string;
  bookId: string;
  periodId: string;
  /** The book's approved policy reference this analysis was prepared under (free text). */
  policyVersion: string;
  currencyCode: string;
  /** Decimal string: approved loss-allowance balance at cutoff. */
  lossAllowanceAmount: string;
  note?: string | null;
  lines: CreateArPortfolioLossAnalysisLineInput[];
}

/** `NOT_POSTED`: no linked posted journal at cutoff. `REVERSED`: its linked journal was reversed
 * by cutoff. `POSTED_LINK`: its linked journal header is posted and live at cutoff; journal lines,
 * source, nature, amount and currency are not verified against this approved effect. */
export type ArPortfolioLossLineEffect = 'NOT_POSTED' | 'REVERSED' | 'POSTED_LINK';

export interface ArPortfolioLossEffectCounts {
  approvedEffectNotPosted: number;
  approvedEffectReversed: number;
  writeOffPathUnsupported: number;
  writeOffOnSettledTitle: number;
  lossLineTitleUnknown: number;
  allowanceTieOutNotEvaluated: boolean;
}

export interface ArPortfolioLossAnalysisLineResponse {
  lineId: string;
  kind: ArPortfolioLossAnalysisLineKind;
  invoiceId: string;
  scheduleId: string | null;
  amount: string;
  journalEntryId: string | null;
  /** Current state of the journal link this line names, read live (never saved). */
  effect: ArPortfolioLossLineEffect;
}

/** `MISSING`: no analysis, or no control-level AR08 review at all. `REJECTED`: the latest review
 * rejected it. `INVALIDATED`: the latest review no longer binds this rule version or source
 * identity (a changed title, source or policy). `VALID`: an ACCEPTED review bound to exactly this
 * analysis and rule version. */
export type ArPortfolioLossReviewState = 'MISSING' | 'REJECTED' | 'INVALIDATED' | 'VALID';

export interface ArPortfolioLossAnalysisResponse {
  analysisId: string;
  entityId: string;
  bookId: string;
  periodId: string;
  policyVersion: string;
  currencyCode: string;
  lossAllowanceAmount: string;
  note: string | null;
  createdByUserId: string;
  createdAt: string;
  reviewState: ArPortfolioLossReviewState;
  effectCounts: ArPortfolioLossEffectCounts;
  lines: ArPortfolioLossAnalysisLineResponse[];
}

/** A scope with no saved analysis still returns its state so clients never infer approval from an
 * empty or failed request. */
export interface ArPortfolioLossReviewMissingResponse {
  analysisId: null;
  entityId: string;
  bookId: string;
  periodId: string;
  policyVersion: null;
  currencyCode: null;
  lossAllowanceAmount: null;
  note: null;
  createdByUserId: null;
  createdAt: null;
  reviewState: 'MISSING';
  effectCounts: ArPortfolioLossEffectCounts;
  lines: [];
}

export interface ArPortfolioLossReviewQuery {
  entityId: string;
  bookId: string;
  periodId: string;
}

export type ArPortfolioLossReviewResponse =
  ArPortfolioLossAnalysisResponse | ArPortfolioLossReviewMissingResponse;
